import type { ReactNode } from 'react';
import type { ImageEditorTexts } from './-components/image-editor';
import { arrayMove } from '@dnd-kit/sortable';
import { useFieldContext, useFieldValue, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { FieldLabelRow } from './field-label-row';
import { MultiImageUpload, type MultiImageItem } from './-components/multi-image-upload';

export type OtherImageItem = {
	/** Server image key (used for submission) */
	existingKey?: string;
	/** Server image display URL */
	existingUrl?: string;
	/** New file upload */
	file?: File;
	/** Alt text per language */
	alt: Record<string, string>;
};

/**
 * Form field for multiple image uploads. Field value is `OtherImageItem[]`.
 *
 * Handles existing server images and new uploads as a unified array.
 * Removing an image simply removes it from the array — no separate tracking needed.
 *
 * @example
 * ```tsx
 * <form.AppField name="otherImages" children={(field) => (
 *   <field.MultiImageUploadField label="Upload">
 *     {(index) => (
 *       <form.AppField name={`otherImages[${index}].alt.en`}
 *         children={(f) => <f.AltTextField placeholder="Describe" />} />
 *     )}
 *   </field.MultiImageUploadField>
 * )} />
 * ```
 */
export default function MultiImageUploadField(props: {
	buttonLabel: string;
	label?: string;
	description?: ReactNode;
	/** Maximum number of images allowed (existing + new). When reached, the drop zone is hidden. */
	maxImages?: number;
	noImagesText?: string;
	cropImageText?: string;
	removeImageText?: string;
	dragToReorderText?: string;
	dropZoneDescription?: string;
	imageEditorTexts?: Partial<ImageEditorTexts>;
	children?: (index: number) => ReactNode;
}) {
	const field = useFieldContext<OtherImageItem[]>();
	const formReadOnly = useFormReadOnly();
	const isOptional = useIsFieldOptional(field.name);
	const items = useFieldValue(field) ?? [];

	// Build unified image list preserving the items' order
	const images: MultiImageItem[] = items
		.map((item): MultiImageItem | null => {
			if (item.existingUrl) return { type: 'existing', url: item.existingUrl };
			if (item.file) return { type: 'new', file: item.file };
			return null;
		})
		.filter((x): x is MultiImageItem => x !== null);

	return (
		<div>
			{props.label && (
				<div className="mb-2">
					<FieldLabelRow
						label={props.label}
						htmlFor={field.name}
						hasError={field.state.meta.isTouched && !field.state.meta.isValid}
						optional={isOptional}
						info={props.description}
					/>
				</div>
			)}
			<MultiImageUpload
				id={field.name}
				images={images}
				maxImages={props.maxImages}
				onAddFiles={(newFiles) => {
					const newItems: OtherImageItem[] = newFiles.map((f) => ({
						file: f,
						alt: { translatable: '' } as Record<string, string>,
					}));
					field.handleChange([...items, ...newItems]);
				}}
				onRemove={(index) => {
					field.handleChange(items.filter((_, i) => i !== index));
				}}
				onReorder={(fromIndex, toIndex) => {
					field.handleChange(arrayMove(items, fromIndex, toIndex));
				}}
				onCrop={(index, croppedFile) => {
					const newItems = [...items];
					newItems[index] = { ...newItems[index]!, file: croppedFile };
					field.handleChange(newItems);
				}}
				label={props.buttonLabel}
				readOnly={formReadOnly}
				noImagesText={props.noImagesText}
				cropImageText={props.cropImageText}
				removeImageText={props.removeImageText}
				dragToReorderText={props.dragToReorderText}
				dropZoneDescription={props.dropZoneDescription}
				imageEditorTexts={props.imageEditorTexts}
				renderImageExtra={props.children}
			/>
		</div>
	);
}
